/**
 * Kyrgyz and Russian (doc 07). The app opens in Kyrgyz for everyone and is
 * never detected from the phone (D-24); a choice made before sign-in is kept
 * on the device, and once signed in her account's locale wins.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { KY_NAMES, weekday, ymd } from '../lib/time';
import type { Locale } from '../types';
import ky from './ky.json';
import ru from './ru.json';

export const LOCALES: Locale[] = ['ky', 'ru'];
const DEVICE_KEY = 'masterday_locale';

export function deviceLocale(): Locale {
  try {
    const stored = localStorage.getItem(DEVICE_KEY);
    return stored === 'ru' || stored === 'ky' ? stored : 'ky';
  } catch {
    return 'ky';
  }
}

export async function setLocale(locale: Locale): Promise<void> {
  try {
    localStorage.setItem(DEVICE_KEY, locale);
  } catch {
    /* private mode: the choice lasts this session only */
  }
  document.documentElement.lang = locale;
  await i18n.changeLanguage(locale);
}

export function initI18n(): typeof i18n {
  const locale = deviceLocale();
  document.documentElement.lang = locale;
  void i18n.use(initReactI18next).init({
    resources: { ky: { translation: ky }, ru: { translation: ru } },
    lng: locale,
    fallbackLng: false, // a missing key must show, not silently fall back (the parity test guards it)
    interpolation: { escapeValue: false },
    returnNull: false,
    showSupportNotice: false,
  });
  return i18n;
}

/** Text for an ApiError code, in the current language (doc 04 §2). */
export function errorText(code: string): string {
  const key = `error.${code}`;
  return i18n.exists(key) ? i18n.t(key) : i18n.t('error.fallback');
}

const ZONE = 'Asia/Bishkek';

/** «четверг, 24 сентября» / «24-сентябрь, бейшемби» (doc 07 §3). */
export function formatLongDate(iso: string, locale: Locale): string {
  if (locale === 'ky') {
    // Not through Intl: see KY_NAMES in lib/time.ts.
    const day = ymd(iso);
    return `${Number(day.slice(8))}-${KY_NAMES.month[Number(day.slice(5, 7)) - 1]}, ${KY_NAMES.weekday[weekday(day)]}`;
  }
  return new Intl.DateTimeFormat(locale, {
    timeZone: ZONE,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso));
}

/** 24-hour HH:MM in Bishkek, whatever the browser's own zone. */
export function formatTime(iso: string): string {
  return new Intl.DateTimeFormat('ru', { timeZone: ZONE, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(
    new Date(iso),
  );
}

/** «7 500» -- an amount without the currency, as the mockup's footers show it. */
export function formatAmount(amount: string, _locale: Locale): string {
  // «2500», no thousands space (owner, 2026-10-04): the thin space Russian puts in
  // «2 500» takes a full cell in the monospaced figures and reads as a gap. Same
  // digits in both languages; without grouping a browser can't print «2,500» either.
  return new Intl.NumberFormat('ru', { maximumFractionDigits: 0, useGrouping: false }).format(Number(amount));
}

/** «2 500 сом» in both languages. Never the currency style, which prints KGS in Russian. */
export function formatMoney(amount: string, locale: Locale): string {
  return `${formatAmount(amount, locale)} ${i18n.t('money.som')}`;
}

export default i18n;
